import { QueryClient } from '@tanstack/react-query';
import type { PersistedClient, Persister } from '@tanstack/query-persist-client-core';
import { getDb, type LearnKidsDb } from '../offline/db';
import { ApiHttpError, NetworkError } from './errors';
import { purgeUserResponseCaches } from './http';

const CACHE_KEY = 'lk.query-cache.v1';

/**
 * Персистер кешу запитів в IndexedDB (та сама база, що й офлайн-черга).
 * Завдяки йому після F5 без мережі дитина бачить свій гаманець, ранг і ціль
 * замість порожнього екрана — з чесною позначкою «дані з кешу».
 */
export function createDexiePersister(db: LearnKidsDb = getDb(), key = CACHE_KEY): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      await db.cache.put({ key, value: client, updatedAt: Date.now() });
    },
    restoreClient: async () => {
      const row = await db.cache.get(key);
      return row?.value as PersistedClient | undefined;
    },
    removeClient: async () => {
      await db.cache.delete(key);
    },
  };
}

/**
 * Забути користувача, який виходить, у всіх трьох сховищах одразу:
 * памʼять (кеш запитів), IndexedDB (персистований зліпок того ж кешу)
 * і Cache Storage (відповіді, збережені service worker-ом).
 *
 * Пропустити бодай одне — значить лишити наступному, хто зайде з цього
 * пристрою, шанс побачити чужий гаманець, імʼя чи аналітику: персистований
 * кеш домальовує дані миттєво, ще до першої відповіді сервера.
 *
 * Черги відповідей у Dexie не чіпаємо НІКОЛИ — там лежить те, що дитина вже
 * відповіла, але сервер ще не підтвердив.
 */
export async function purgeUserCaches(
  client: QueryClient,
  db: LearnKidsDb = getDb(),
  key = CACHE_KEY,
): Promise<void> {
  client.clear();
  try {
    await db.cache.delete(key);
  } catch {
    // Немає IndexedDB (приватний режим, квота) — не привід зривати вихід.
  }
  await purgeUserResponseCaches();
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 7 * 24 * 3600_000,
        refetchOnWindowFocus: true,
        networkMode: 'offlineFirst',
        retry: (failureCount, error) => {
          // 401 і будь-яку «остаточну» 4xx повторювати безглуздо.
          if (error instanceof ApiHttpError && (error.isUnauthorized || error.isPermanent)) {
            return false;
          }
          // Мережеві — повторюємо помірно; далі рятує кеш і офлайн-екран.
          if (error instanceof NetworkError) return failureCount < 2;
          return failureCount < 3;
        },
        retryDelay: (attempt) => Math.min(8_000, 600 * 2 ** attempt),
      },
      mutations: {
        networkMode: 'offlineFirst',
        retry: 0,
      },
    },
  });
}
